import { subscribeDeckEvents } from '../api/deck-events-shared';
import { useEffect, useRef } from 'react';

/**
 * Long-lived SSE connection to the deck's event stream.
 *
 * CF rebuild adapter notes:
 *
 * - Original PP/arx exposed GET /api/decks/:id/chat with one
 *   un-named SSE channel; events were typed via `event.type`.
 * - CF exposes GET /api/decks/:id/events with NAMED SSE events:
 *     event: message    data: {id, role, content, ...}
 *     event: activity   data: {kind, payload}
 *     event: turn       data: {turn_id, status}
 * - We subscribe to those named channels here and translate them
 *   into the ChatEvent shape the consumers (App.tsx etc.) expect.
 *
 * Translation table:
 *   message (role=user)              → user_message
 *   message (role=agent)             → ignored (deltas surface as content)
 *   activity message_delta           → content
 *   activity agent_thinking          → thinking
 *   activity turn_ended              → done
 *   activity error                   → error
 *   turn (status=running|idle)       → ignored (UI driven by other events)
 */

export interface ChatEvent {
  type: string;
  text?: string;
  stream?: string;
  agent?: string;
  message?: string;
  session_id?: string;
  exit_code?: number;
  duration_seconds?: number;
  reason?: string;
  [key: string]: unknown;
}

export interface DeckChatHandlers {
  onContent?: (
    text: string,
    agent: string,
    turnId: string,
    agentId: string,
    messageId?: string,
  ) => void;
  onDone?: (event: ChatEvent) => void;
  onError?: (message: string, agent: string) => void;
  onConnected?: (isReconnect: boolean) => void;
  onDisconnected?: (reason: string) => void;
  onStreamDropped?: (consecutiveErrors: number) => void;
  onUserMessage?: (
    messageId: string,
    content: string,
    files?: string[],
    attachments?: { r2_key: string; mime: string; name: string; size: number }[],
    identity?: {author_user_id?:string;author_name?:string;client_request_id?:string;intent?:string},
  ) => void;
  onSystemMessage?: (id:string,content:string,turnId:string)=>void;
  onSessionLimit?: () => void;
}

interface CfMessageEvent {
  author_user_id?:string;author_name?:string;client_request_id?:string;intent?:string;
  id: string;
  turn_id: string;
  role: 'user' | 'agent' | 'system';
  content: string;
  created_at: number;
  attachments?: { r2_key: string; mime: string; name: string; size: number }[];
}

interface CfActivityEvent {
  id?: string;
  turn_id?: string;
  kind: string;
  payload: {
    message_id?: string;
    delta?: string;
    detail?: string;
    message?: string;
  };
}

const AGENT_NAME = 'pp-writer';
const MAX_RETRY_DELAY_MS = 10_000;

export function useDeckChat(
  deckId: string | null,
  handlers: DeckChatHandlers,
  reconnectKey: number = 0,
) {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!deckId) return;

    let consecutiveErrors = 0;
    let currentTurnId = '';

    const sub = subscribeDeckEvents(deckId);

    sub.onOpen((reconnected) => {
      consecutiveErrors = 0;
      handlersRef.current.onConnected?.(reconnected);
    });

    sub.on('message', (e) => {
      try {
        const m = JSON.parse(e.data) as CfMessageEvent;
        if (m.role === 'user') {
          handlersRef.current.onUserMessage?.(
            m.id,
            m.content,
            [],
            Array.isArray(m.attachments) ? m.attachments : undefined,
            {author_user_id:m.author_user_id,author_name:m.author_name,client_request_id:m.client_request_id,intent:m.intent},
          );
        } else if(m.role === 'system'){
          handlersRef.current.onSystemMessage?.(m.id,m.content,m.turn_id);
        } else if (m.role === 'agent') {
          // Empty agent row arrives at turn start; we surface
          // its turn_id but no content. Deltas drive the UI.
          currentTurnId = m.turn_id;
        }
      } catch {
        /* ignore */
      }
    });

    sub.on('activity', (e) => {
      try {
        const a = JSON.parse(e.data) as CfActivityEvent;
        const turnId = a.turn_id || currentTurnId;
        const h = handlersRef.current;
        if (a.kind === 'message_delta' && typeof a.payload.delta === 'string') {
          h.onContent?.(a.payload.delta, AGENT_NAME, turnId, AGENT_NAME, a.payload.message_id);
        } else if (a.kind === 'agent_thinking') {
          // PP frontend doesn't have a dedicated 'thinking' channel
          // visible in the chat; the activity panel uses it. We
          // surface it as a chat-event for parity.
          h.onContent?.('', AGENT_NAME, turnId, AGENT_NAME);
        } else if (a.kind === 'turn_ended') {
          h.onDone?.({ type: 'done', turn_id: turnId, agent: AGENT_NAME });
        } else if (a.kind === 'error') {
          h.onError?.(a.payload.message || 'Agent error', AGENT_NAME);
        }
      } catch {
        /* ignore */
      }
    });

    sub.onError(() => {
      consecutiveErrors += 1;
      handlersRef.current.onStreamDropped?.(consecutiveErrors);
      if (consecutiveErrors >= 3) {
        handlersRef.current.onSessionLimit?.();
      }
    });

    return () => {
      sub.close();
    };
  }, [deckId, reconnectKey]);
}
